import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Panel, TrendBadge } from "../components/common";
import { ScoreGauge } from "../components/ScoreGauge";
import { scoreMeta, chgColor, pct, fmt, subScoreOrder, corrColor, classLabel } from "../lib/format";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Legend,
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, ReferenceLine,
} from "recharts";
import { GitCompareArrows, ArrowLeftRight, Sparkles, Activity, Layers, History, ExternalLink } from "lucide-react";

const A_COLOR = "#00E5FF";
const B_COLOR = "#F59E0B";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CORR_WINDOWS = [["20", "20D"], ["60", "60D"], ["120", "120D"], ["252", "1Y"], ["756", "3Y"], ["1260", "5Y"]];

export default function Compare() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const [universe, setUniverse] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const a = (sp.get("a") || "SPY").toUpperCase();
  const b = (sp.get("b") || "QQQ").toUpperCase();

  useEffect(() => {
    api.get("/assets").then((r) => setUniverse(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setData(null); setErr(false);
    api.get("/compare", { params: { a, b } }).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [a, b]);

  const setPair = (na, nb) => setSp({ a: na, b: nb });
  const swap = () => setPair(b, a);

  const radar = useMemo(() => {
    if (!data) return [];
    return subScoreOrder.map(([k, l]) => ({ dim: l, a: data.a.scores[k], b: data.b.scores[k] }));
  }, [data]);

  const seasData = useMemo(() => {
    if (!data) return [];
    return MONTHS.map((nm, i) => ({
      m: nm,
      a: data.a.seasonality?.months?.[String(i + 1)]?.avg ?? 0,
      b: data.b.seasonality?.months?.[String(i + 1)]?.avg ?? 0,
    }));
  }, [data]);

  return (
    <div className="space-y-5" data-testid="compare-page">
      <div>
        <div className="eyebrow">multi-asset engine</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <GitCompareArrows size={26} className="text-[#00E5FF]" /> Head-to-Head Comparison
        </h1>
      </div>

      {/* selectors */}
      <div className="panel p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <Selector label="Asset A" value={a} color={A_COLOR} universe={universe} exclude={b}
          onChange={(v) => setPair(v, b)} testid="compare-select-a" />
        <button data-testid="compare-swap" onClick={swap} title="Swap"
          className="h-10 w-10 shrink-0 self-center rounded-md flex items-center justify-center border border-[#334155] hover:bg-[#00E5FF12] hover:border-[#00E5FF44] hover:text-[#00E5FF] transition-colors">
          <ArrowLeftRight size={16} />
        </button>
        <Selector label="Asset B" value={b} color={B_COLOR} universe={universe} exclude={a}
          onChange={(v) => setPair(a, v)} testid="compare-select-b" />
      </div>

      {err && <div className="font-mono text-sm text-[#EF4444]">Could not load comparison. Pick two valid assets.</div>}
      {!data && !err && <div className="font-mono text-sm text-slate-500 animate-pulse">comparing {a} vs {b}…</div>}

      {data && (
        <>
          {/* score header cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="compare-score-cards">
            <AssetCard s={data.a} color={A_COLOR} nav={nav} testid="compare-card-a" />
            <AssetCard s={data.b} color={B_COLOR} nav={nav} testid="compare-card-b" />
          </div>

          {/* radar + correlation */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Panel title="Sub-Score Radar — overlaid" icon={<Sparkles size={13} />}>
              <ResponsiveContainer width="100%" height={340}>
                <RadarChart data={radar} outerRadius="70%">
                  <PolarGrid stroke="#1B243B" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" }} />
                  <Radar name={data.a.symbol} dataKey="a" stroke={A_COLOR} fill={A_COLOR} fillOpacity={0.22} strokeWidth={2} />
                  <Radar name={data.b.symbol} dataKey="b" stroke={B_COLOR} fill={B_COLOR} fillOpacity={0.18} strokeWidth={2} />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                </RadarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Pairwise Correlation" icon={<Layers size={13} />}
              right={data.correlation && <span className="font-mono text-[11px] text-slate-400">current 60D <b style={{ color: corrColor(data.correlation.current) === "#141C2E" ? "#94A3B8" : (data.correlation.current >= 0 ? "#10B981" : "#EF4444") }}>{fmt(data.correlation.current, 2)}</b></span>}>
              {!data.correlation ? <div className="text-xs text-slate-500 font-mono">no correlation data</div> : (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4" data-testid="compare-corr-windows">
                    {CORR_WINDOWS.map(([w, l]) => {
                      const v = data.correlation.windows[w];
                      return (
                        <div key={w} className="rounded-md p-2 text-center border" style={{ borderColor: "#1E293B", background: corrColor(v) }}>
                          <div className="eyebrow">{l}</div>
                          <div className="font-mono font-bold tnum text-sm text-slate-100">{v != null ? Number(v).toFixed(2) : "—"}</div>
                        </div>
                      );
                    })}
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={data.correlation.rolling} margin={{ top: 6, right: 10, left: -14, bottom: 0 }}>
                      <CartesianGrid stroke="#1B243B" vertical={false} />
                      <XAxis dataKey="t" tick={{ fill: "#64748B", fontSize: 9, fontFamily: "JetBrains Mono" }} minTickGap={50} />
                      <YAxis domain={[-1, 1]} tick={{ fill: "#64748B", fontSize: 9, fontFamily: "JetBrains Mono" }} />
                      <Tooltip contentStyle={{ background: "#0E131F", border: "1px solid #334155", borderRadius: 6, fontSize: 11, fontFamily: "JetBrains Mono" }} />
                      <ReferenceLine y={0} stroke="#334155" />
                      <Line type="monotone" dataKey="v" name="60D corr" stroke="#8B5CF6" strokeWidth={1.8} dot={false} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </>
              )}
            </Panel>
          </div>

          {/* trends */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="compare-trends">
            {[data.a, data.b].map((s, idx) => (
              <Panel key={s.symbol} title={`${s.symbol} · Trend Structure`} icon={<Activity size={13} style={{ color: idx === 0 ? A_COLOR : B_COLOR }} />}>
                <div className="grid grid-cols-3 gap-3">
                  <TrendBadge trend={s.trends.short} label="Short (20/50)" testid={`compare-trend-${s.symbol}-short`} />
                  <TrendBadge trend={s.trends.medium} label="Medium (50/100)" testid={`compare-trend-${s.symbol}-medium`} />
                  <TrendBadge trend={s.trends.long} label="Long (100/200)" testid={`compare-trend-${s.symbol}-long`} />
                </div>
                <div className="grid grid-cols-4 gap-3 mt-3 text-center">
                  {[["1M", s.technical.mom_1m], ["3M", s.technical.mom_3m], ["6M", s.technical.mom_6m], ["12M", s.technical.mom_12m]].map(([l, v]) => (
                    <div key={l}>
                      <div className="eyebrow">Mom {l}</div>
                      <div className="font-mono font-bold tnum text-sm" style={{ color: chgColor(v) }}>{pct(v, 1)}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            ))}
          </div>

          {/* seasonality */}
          <Panel title="Seasonality — Avg Monthly Return (overlaid)" icon={<History size={13} />}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={seasData} margin={{ top: 8, right: 8, left: -14, bottom: 0 }} barGap={2}>
                <CartesianGrid stroke="#1B243B" vertical={false} />
                <XAxis dataKey="m" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <Tooltip contentStyle={{ background: "#0E131F", border: "1px solid #334155", borderRadius: 6, fontSize: 12, fontFamily: "JetBrains Mono" }} cursor={{ fill: "#ffffff08" }} />
                <ReferenceLine y={0} stroke="#334155" />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Bar dataKey="a" name={data.a.symbol} fill={A_COLOR} fillOpacity={0.85} radius={[2, 2, 0, 0]} />
                <Bar dataKey="b" name={data.b.symbol} fill={B_COLOR} fillOpacity={0.85} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </>
      )}
    </div>
  );
}

function Selector({ label, value, color, universe, exclude, onChange, testid }) {
  return (
    <div className="flex-1">
      <div className="eyebrow mb-1" style={{ color }}>{label}</div>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full panel-2 rounded-md px-3 py-2.5 text-sm font-mono outline-none focus:border-[#00E5FF] transition-colors"
        style={{ borderLeft: `3px solid ${color}` }}>
        {universe.filter((u) => u.symbol !== exclude).map((u) => (
          <option key={u.symbol} value={u.symbol}>{`${u.symbol} · ${u.name}`}</option>
        ))}
      </select>
    </div>
  );
}

function AssetCard({ s, color, nav, testid }) {
  const m = scoreMeta(s.smart_quant_score);
  return (
    <div className="panel p-5 fade-up" data-testid={testid} style={{ borderColor: `${color}44` }}>
      <div className="flex items-center gap-5">
        <ScoreGauge value={s.smart_quant_score} size={120} thickness={11} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button onClick={() => nav(`/asset/${s.symbol}`)} className="font-display text-3xl font-extrabold tracking-tight hover:text-[#00E5FF] transition-colors flex items-center gap-1.5">
              {s.symbol} <ExternalLink size={15} className="text-slate-500" />
            </button>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-[#334155] text-slate-300">{classLabel[s.asset_class]}</span>
          </div>
          <div className="text-slate-400 text-xs mt-0.5 truncate">{s.name}</div>
          <div className="flex items-end gap-3 mt-2">
            <span className="font-mono text-2xl font-extrabold tnum">{fmt(s.price)}</span>
            <span className="font-mono text-sm tnum mb-0.5" style={{ color: chgColor(s.change_1d) }}>{pct(s.change_1d)}</span>
          </div>
          <span className="inline-block mt-2 text-xs font-semibold px-2.5 py-0.5 rounded" style={{ color: m.color, background: `${m.color}18` }}>{s.classification}</span>
        </div>
      </div>
    </div>
  );
}
